/** Structured preview for unit.meta.coachOverlay / learnerEvidence (desk enrich-style UI). */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown, max = 400): string {
  const s = String(value ?? '').trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Human labels for overlay enum / kind codes (desk UI; no raw eng keys). */
export function overlayKindLabelZh(kind: string): string {
  const key = String(kind || '').trim();
  if (!key) return '';
  const map: Record<string, string> = {
    strike_rewrite: '划改重写',
    side_check: '旁路验算',
    multi_attempt: '多次尝试',
    diagram_draft: '草图草稿',
    other: '其它',
    admonition: '嘱咐',
    trap: '陷阱',
    strategy: '策略',
    identity: '恒等式',
    method: '手法',
    annotation: '标注',
    note: '旁注',
    box: '框选',
    circle: '圈注',
    underline: '下划',
    arrow: '指向',
  };
  return map[key] || map[key.toLowerCase()] || key.replace(/_/g, ' ');
}

export function overlayInkRoleLabelZh(role: string): string {
  const key = String(role || '').trim().toLowerCase();
  if (!key) return '';
  const map: Record<string, string> = {
    printed: '印刷',
    coach: '教练笔',
    learner: '学情笔',
    unknown: '未判',
  };
  return map[key] || role;
}

/** Overlay / enrich job status for desk summaries. */
export function overlayJobStatusLabelZh(status: string): string {
  const key = String(status || '').trim().toLowerCase();
  if (!key) return '';
  const map: Record<string, string> = {
    succeeded: '已完成',
    failed: '失败',
    running: '进行中',
    pending: '待处理',
    skipped: '已跳过',
  };
  return map[key] || status;
}


export type OverlayAnnotationPreview = {
  id: string;
  inkRole: string;
  kind: string;
  label: string;
  text: string;
  boxLabel: string;
};

export type CoachOverlayPreview = {
  summaryLabel: string;
  version: string;
  model: string;
  admonitions: Array<{ text: string; annotationIds: string[] }>;
  traps: Array<{ title: string; wrongHint: string; rightHint: string; annotationIds: string[] }>;
  strategyTags: string[];
  identityBank: Array<{ expr: string; annotationIds: string[] }>;
  methodVariants: Array<{ problemRef: string; methods: string[] }>;
  annotations: OverlayAnnotationPreview[];
  hasContent: boolean;
};

export type LearnerOverlayPreview = {
  summaryLabel: string;
  version: string;
  model: string;
  struggleSignals: Array<{ kind: string; summary: string; annotationIds: string[] }>;
  scratchPaths: Array<{ summary: string; annotationIds: string[] }>;
  annotations: OverlayAnnotationPreview[];
  hasContent: boolean;
};

function parseAnnotation(raw: unknown, index: number): OverlayAnnotationPreview | null {
  const row = asRecord(raw);
  if (!row) return null;
  const box = asRecord(row.box);
  const boxLabel = box
    ? `x=${Number(box.x).toFixed(2)} y=${Number(box.y).toFixed(2)} w=${Number(box.w).toFixed(2)} h=${Number(box.h).toFixed(2)}`
    : '';
  return {
    id: str(row.id || `a${index + 1}`, 40),
    inkRole: str(row.inkRole, 24),
    kind: str(row.kind, 40),
    label: str(row.label, 80),
    text: str(row.text, 240),
    boxLabel,
  };
}

function parseAnnotations(raw: unknown): OverlayAnnotationPreview[] {
  return asArray(raw)
    .map((item, i) => parseAnnotation(item, i))
    .filter((x): x is OverlayAnnotationPreview => Boolean(x));
}

export function buildCoachOverlayPreview(
  overlay: Record<string, unknown> | null | undefined,
): CoachOverlayPreview | null {
  if (!overlay) return null;
  const version = str(overlay.version, 40);
  const model = str(overlay.model, 60);
  const admonitions = asArray(overlay.admonitions).map((item) => {
    const row = asRecord(item);
    return {
      text: str(row?.text, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.text);
  const traps = asArray(overlay.traps).map((item) => {
    const row = asRecord(item);
    return {
      title: str(row?.title, 80),
      wrongHint: str(row?.wrongHint, 200),
      rightHint: str(row?.rightHint, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.title || x.wrongHint || x.rightHint);
  const strategyTags = asArray(overlay.strategyTags).map((t) => str(t, 40)).filter(Boolean);
  const identityBank = asArray(overlay.identityBank).map((item) => {
    const row = asRecord(item);
    return {
      expr: str(row?.expr, 120),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.expr);
  const methodVariants = asArray(overlay.methodVariants).map((item) => {
    const row = asRecord(item);
    return {
      problemRef: str(row?.problemRef, 40),
      methods: asArray(row?.methods).map((m) => str(m, 120)).filter(Boolean),
    };
  }).filter((x) => x.problemRef || x.methods.length);
  const annotations = parseAnnotations(overlay.annotations);
  const hasContent = Boolean(
    admonitions.length
    || traps.length
    || strategyTags.length
    || identityBank.length
    || methodVariants.length
    || annotations.length,
  );
  const bits = [
    annotations.length ? `标注 ${annotations.length}` : '',
    traps.length ? `陷阱 ${traps.length}` : '',
    admonitions.length ? `嘱咐 ${admonitions.length}` : '',
    strategyTags.length ? `策略 ${strategyTags.length}` : '',
  ].filter(Boolean);
  return {
    summaryLabel: bits.length ? `教练层 · ${bits.join(' · ')}` : '教练层',
    version,
    model,
    admonitions,
    traps,
    strategyTags,
    identityBank,
    methodVariants,
    annotations,
    hasContent,
  };
}

export function buildLearnerOverlayPreview(
  evidence: Record<string, unknown> | null | undefined,
): LearnerOverlayPreview | null {
  if (!evidence) return null;
  const version = str(evidence.version, 40);
  const model = str(evidence.model, 60);
  const struggleSignals = asArray(evidence.struggleSignals).map((item) => {
    const row = asRecord(item);
    return {
      kind: str(row?.kind, 40),
      summary: str(row?.summary, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.summary || x.kind);
  const scratchPaths = asArray(evidence.scratchPaths).map((item) => {
    const row = asRecord(item);
    return {
      summary: str(row?.summary, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.summary);
  const annotations = parseAnnotations(evidence.annotations);
  const hasContent = Boolean(
    struggleSignals.length || scratchPaths.length || annotations.length,
  );
  const bits = [
    annotations.length ? `标注 ${annotations.length}` : '',
    struggleSignals.length ? `挣扎 ${struggleSignals.length}` : '',
    scratchPaths.length ? `草稿 ${scratchPaths.length}` : '',
  ].filter(Boolean);
  return {
    summaryLabel: bits.length ? `学习层 · ${bits.join(' · ')}` : '学习层',
    version,
    model,
    struggleSignals,
    scratchPaths,
    annotations,
    hasContent,
  };
}
